/**
 * The document, drawn.
 *
 * Deliberately dumb: it renders `EstimateDocument` and computes nothing. If a
 * figure is wrong it is wrong in `buildDocument` or in the calculation engine,
 * and there is exactly one place to look.
 *
 * The same component is what prints. There is no separate print template,
 * because two templates drift and the customer gets the one that drifted.
 *
 * Every free-text field is wrapped in `<bdi>`. A Gulf shop's catalog is often
 * written in English, and Latin text dropped into an Arabic document has its
 * trailing full stop reordered to the front by the bidi algorithm — so
 * "Bumper cover, front." renders as ".Bumper cover, front". `<bdi>` isolates
 * each field and lets the browser detect its direction per value.
 */

import { Fragment, useState } from 'react';
import { useSession } from '../app/SessionProvider.js';
import { direction } from '../intl/i18n.js';
import { localDate } from '../intl/dates.js';
import type { EstimateDocument } from './estimate.js';

const cls = (base: string, blank: boolean) => (blank ? `${base} blank` : base);

export function EstimateSheet({ doc }: { doc: EstimateDocument }) {
  const { money } = useSession();
  const dir = direction(doc.language);
  // Which row's evidence is open, by line number. One at a time — this is a
  // document being read, not a review screen with something to compare.
  const [openRow, setOpenRow] = useState<number | null>(null);

  return (
    <article className="sheet" dir={dir} lang={doc.language}>
      <header className="sheet-h">
        <div>
          <div className="sheet-shop"><bdi>{doc.shopName}</bdi></div>
          <h1>{doc.title}</h1>
        </div>
        <div className="sheet-ref">
          <div className="mono">{doc.reference}</div>
          {doc.issued && (
            <div className="mono small muted">
              v{doc.issued.version} · {localDate(doc.issued.at, doc.language)}
            </div>
          )}
        </div>
      </header>

      <div className="sheet-parties">
        <div>
          <span className="lbl">Customer</span>
          <b><bdi>{doc.customerName}</bdi></b>
          {doc.terms && <div className="muted small"><bdi>{doc.terms}</bdi></div>}
        </div>
        <div>
          <span className="lbl">Vehicle</span>
          <b><bdi>{doc.vehicle}</bdi></b>
          {doc.impact && <div className="muted small"><bdi>{doc.impact}</bdi></div>}
        </div>
      </div>

      <table className="sheet-lines">
        <thead>
          <tr>
            <th className="num">#</th>
            <th>Operation</th>
            <th>Description</th>
            <th>Part no</th>
            <th className="num">Qty</th>
            <th className="num">Unit</th>
            <th className="num">Hours</th>
            <th className="num">Amount</th>
          </tr>
        </thead>
        <tbody>
          {doc.rows.length === 0 ? (
            <tr><td colSpan={8} className="empty">No lines on this estimate.</td></tr>
          ) : doc.rows.map((r) => {
            const hasEvidence = Boolean(
              r.whyThis && (r.whyThis.observations.length > 0 || r.whyThis.photos.length > 0));
            const open = hasEvidence && openRow === r.no;
            return (
            <Fragment key={r.no}>
            <tr className={r.suppressed ? 'struck' : undefined}>
              {/* data-label is what lets the table restack as cards on a phone.
                  A customer reads this on the device the email arrived on. */}
              <td className="num" data-label="#">{r.no}</td>
              <td data-label="Operation"><bdi>{r.operation}</bdi></td>
              <td data-label="Description">
                <bdi>{r.description}</bdi>
                {r.suppressed && <span className="flag plain">not charged</span>}
                {/* Only where real evidence exists — a hand-added line, a
                    refinish line, or one issued before this existed gets no
                    button rather than one that opens on nothing. */}
                {hasEvidence && (
                  <button type="button" className="whytoggle"
                          aria-expanded={open}
                          onClick={() => setOpenRow(open ? null : r.no)}>
                    {open ? 'Hide' : 'Why this?'}
                  </button>
                )}
              </td>
              {/* Part numbers stay left-to-right even in an Arabic document. */}
              {/* `blank` marks a cell whose value is "not applicable". On paper a
                  dash keeps the columns aligned; on a phone, where the row is a
                  stack, five dashes are five lines of nothing — so they go. */}
              <td className={cls('mono', r.partNumber === null)} dir="ltr" data-label="Part no">
                {r.partNumber ?? '—'}</td>
              <td className={cls('num', r.partless)} data-label="Qty">
                {r.partless ? '—' : r.quantity}</td>
              <td className={cls('num', r.unitPrice === null)} data-label="Unit">
                {r.unitPrice === null ? '—' : money(r.unitPrice)}</td>
              <td className={cls('num', r.hours === 0)} data-label="Hours">
                {r.hours === 0 ? '—' : r.hours.toFixed(1)}</td>
              <td className={cls('num', r.partless)} data-label="Amount">
                {r.partless ? '—' : money(r.amount)}</td>
            </tr>
            {/* A whole extra row rather than something absolutely positioned:
                the table restacks as cards on a phone (data-label), and an
                overlay would not restack with it. `no-print` because this is
                for the screen, never the paper — the sheet's whole point is
                to be the same document whether printed or approved. */}
            {open && (
              <tr className="whyrow no-print">
                <td colSpan={8}>
                  <div className="whypanel">
                    {r.whyThis!.photos.map((src) => (
                      <img key={src} src={src} alt="" loading="lazy" />
                    ))}
                    {r.whyThis!.observations.length > 0 && (
                      <ul>
                        {r.whyThis!.observations.map((o, i) => <li key={i}><bdi>{o}</bdi></li>)}
                      </ul>
                    )}
                  </div>
                </td>
              </tr>
            )}
            </Fragment>
            );
          })}
        </tbody>
      </table>

      <div className="sheet-totals">
        {doc.totals.map((row) => (
          <div key={row.label} className={row.emphasis ? 'row grand' : 'row'}>
            <span>{row.label}</span>
            <span>{money(row.amount)}</span>
          </div>
        ))}
      </div>

      {doc.suppressedNote && (
        <p className="sheet-note muted small">{doc.suppressedNote}</p>
      )}

      {doc.callToAction && (
        <div className="sheet-cta">
          <b>Your approval is needed</b>
          <p><bdi>{doc.callToAction}</bdi></p>
        </div>
      )}

      <footer className="sheet-f muted small">
        <bdi>{doc.shopName}</bdi> · {doc.reference} · {doc.currencyCode}
        {doc.issued
          ? <> · <span className="mono">document {doc.issued.hash}</span> — this version
              cannot be altered</>
          : <> · <b>DRAFT — not yet issued</b></>}
      </footer>
    </article>
  );
}

